import { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "wouter";
import { ChevronRight, MapPin } from "lucide-react";
import {
  useProfile,
  useEvents,
  useReservations,
  useNeighborhoods,
  useSearchCities,
} from "@/lib/api";
import { UserAPI } from "@shared/types/api";
import {
  formatTimeToDisplay,
  eventTimeToDaysjs,
  parseUTCDate,
} from "@/utils/dateUtils";
import { getEventTypeLabel, getEventTypeEmoji } from "@/utils/eventTypeUtils";
import { CitySelectionModal } from "./CitySelectionModal";
import CityUnlockSection from "./CityUnlockSection";
import dayjs from "dayjs";
import { BOOKING_CUTOFF_HOURS } from "@shared/constants";
import { useT } from "@/lib/i18n";
import dinnerImg from "@/assets/images/img_dinner.webp";
import ImageHeader from "./ux/ImageHeader";
import { DateBadge } from "@/components/ux/DateBadge";
import { Loader } from '@/components/Loader';

interface BookingSectionProps {
  /** Max number of events to show (undefined = show all) */
  maxEvents?: number;
  /** Whether to show a "View All" link */
  showViewAll?: boolean;
}

export default function BookingSection({
  maxEvents,
  showViewAll = false,
}: BookingSectionProps) {
  const { t } = useT();
  const { data: profile, isLoading: profileLoading } = useProfile();
  const [selectedCityId, setSelectedCityId] = useState("usa_nyc");
  const [showCityModal, setShowCityModal] = useState(false);
  const [hasInitializedCity, setHasInitializedCity] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (profile?.data?.user?.city && !hasInitializedCity) {
      setSelectedCityId(profile.data.user.city);
      setHasInitializedCity(true);
    } else if (!profileLoading && !hasInitializedCity) {
      setHasInitializedCity(true);
    }
  }, [profile, hasInitializedCity, profileLoading]);

  const { data: eventsData, isLoading: eventsLoading } = useEvents();
  const { data: reservationsData } = useReservations();
  const { data: citiesData } = useSearchCities("", selectedCityId);
  const { data: neighborhoodsData, isLoading: neighborhoodsLoading } =
    useNeighborhoods(undefined, selectedCityId);

  const cities = citiesData?.data || [];
  const selectedCity = cities.find((c) => c.id === selectedCityId);
  const activeNeighborhoods = useMemo(
    () => neighborhoodsData?.data || [],
    [neighborhoodsData],
  );

  const userReservations = reservationsData?.data || [];
  const activeReservations = userReservations
    .filter(
      (r: UserAPI.Reservation) =>
        r.status === "active" &&
        (r.feedbackCompleted == null ||
          eventTimeToDaysjs(r.eventTime).add(7, "days").isAfter(dayjs())),
    )
    .sort((a, b) => a.eventTime.date.localeCompare(b.eventTime.date));

  const upcomingEvents = useMemo(() => {
    return (eventsData?.data || []).filter((event) => {
      const eventTime = eventTimeToDaysjs(event);
      const beforeBookingCutoff = eventTime
        .subtract(BOOKING_CUTOFF_HOURS, "hours")
        .isAfter(dayjs());
      const hasActiveReservation = activeReservations.some(
        (r: UserAPI.Reservation) => r.eventTimeId === event.id,
      );

      if (!beforeBookingCutoff || hasActiveReservation) return false;

      if (activeNeighborhoods.length === 0) return false;

      const isValidForCity = activeNeighborhoods.some((n: any) => {
        if (event.neighborhoodIds && !event.neighborhoodIds.includes(n.id))
          return false;
        if (!n.launchedAfter) return true;
        return event.date > n.launchedAfter;
      });

      return isValidForCity;
    });
  }, [eventsData?.data, activeReservations, activeNeighborhoods]);

  const shouldLimit = maxEvents != null && !showAll;
  const displayedEvents = shouldLimit
    ? upcomingEvents.slice(0, maxEvents)
    : upcomingEvents;
  const canExpand =
    showViewAll &&
    maxEvents != null &&
    !showAll &&
    upcomingEvents.length > maxEvents;

  return (
    <>
      <ImageHeader
        title={t("dashboard.bookNextDinner")}
        imageSrc={dinnerImg}
        className="mb-3"
        right={
          <button
            onClick={() => setShowCityModal(true)}
            className="flex items-center whitespace-nowrap text-white text-sm font-medium px-3 py-1.5 rounded-full bg-black/80 hover:bg-black/75 backdrop-blur-md border border-white/15 transition-colors"
          >
            <MapPin className="h-4 w-4 inline-block mr-1" />
            {selectedCity?.name || t("dashboard.selectCity")}
          </button>
        }
      />

      {eventsLoading || neighborhoodsLoading ? (
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <Loader />
          </div>
        </div>
      ) : displayedEvents.length > 0 ? (
        <div>
          <div className="grid md:grid-cols-2 gap-2">
            {displayedEvents.map((event: any, index: number) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.1 }}
              >
                <Link
                  href={`/booking?eventId=${event.id}&city=${selectedCityId}`}
                  className="card p-2 block hover:border-primary-500/50 transition-all cursor-pointer"
                >
                  {(() => {
                    const parsed = parseUTCDate(event.date);
                    return (
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-4 min-w-0">
                          <DateBadge date={event.date} size="xl" />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 text-md font-semibold leading-tight">
                              <span>{getEventTypeEmoji(event.type)}</span>
                              <span>{getEventTypeLabel(event.type)}</span>
                            </div>
                            <div className="text-sm text-gray-700 dark:text-gray-200 mt-0.5">
                              {parsed.weekday} ·{" "}
                              {formatTimeToDisplay(event.time)}
                            </div>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 shrink-0" />
                      </div>
                    );
                  })()}
                </Link>
              </motion.div>
            ))}
          </div>
          {canExpand && (
            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="btn-ghost"
              >
                {t("common.buttons.viewAll")}
              </button>
            </div>
          )}
        </div>
      ) : (
        <CityUnlockSection cityLaunched={activeNeighborhoods.length > 0} />
      )}

      <CitySelectionModal
        isOpen={showCityModal}
        onClose={() => setShowCityModal(false)}
        selectedCityId={selectedCityId}
        onSelectCity={setSelectedCityId}
      />
    </>
  );
}
